import { patchState, signalStore, withMethods, withState, withHooks } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject, effect, untracked } from '@angular/core';
import { EMPTY, Observable, pipe } from 'rxjs';
import { catchError, retry, switchMap, tap } from 'rxjs/operators';
import { LocalStorageFacade } from '@application/localstorage';
import { ClientNotificationStore, EClientNotificationsEventTypes } from '@shared/push-notification';
import { EventProgressService } from './infra/event-progress.service';
import { ETypeAutomation } from "@crm/data-access";


import { DateTime } from 'luxon';

export interface IEventProgress {
  eventId: string;
  /**
   * Optional sub-label. CRM events pass an `ETypeAutomation` (rendered via a type map);
   * other producers (e.g. contracts) may pass a translate key string or omit it.
   */
  title?: ETypeAutomation | string;
  origin?: string;
  progress: number;
  minimized: boolean;
  updatedAt?: number;
}

export interface IEventProgressStore {
  activeEvents: IEventProgress[];
  widgetPosition: { x: number; y: number };
  isExpanded: boolean;
}

export const initialEventProgressStore: IEventProgressStore = {
  activeEvents: [],
  widgetPosition: { x: -15, y: -160 },
  isExpanded: true,
};

const STORAGE_KEY = 'event-progress-state';

const HUB_RETRY_DELAY_MS = 5000;
const HUB_RETRY_COUNT = 4;

const keepHubAlive = <T>(source$: Observable<T>): Observable<T> =>
  source$.pipe(
    retry({ count: HUB_RETRY_COUNT, delay: HUB_RETRY_DELAY_MS, resetOnSuccess: true }),
    catchError(() => EMPTY)
  );

interface IPersistedEvent {
  eventId: string;
  title?: ETypeAutomation | string;
  origin?: string;
  minimized: boolean;
  updatedAt?: number;
}

interface IPersistedState {
  events: IPersistedEvent[];
  position: { x: number; y: number };
  isExpanded: boolean;
}

export const EventProgressStore = signalStore(
  { providedIn: 'root' },
  withState(initialEventProgressStore),
  withMethods((
    state, 
    localStorageFacade = inject(LocalStorageFacade),
    ) => ({
    loadFromStorage() {
      const data = localStorageFacade.getItem(STORAGE_KEY);
      if (data) {
        try {
          const persisted: IPersistedState = JSON.parse(data);
          if (persisted && typeof persisted === 'object') {
            const events: IEventProgress[] = (persisted.events || []).map(p => ({
              ...p,
              updatedAt: p.updatedAt ?? DateTime.now().toMillis(),
              progress: 0 
            }));
            patchState(state, { 
              activeEvents: events,
              widgetPosition: persisted.position || initialEventProgressStore.widgetPosition,
              isExpanded: typeof persisted.isExpanded === 'boolean' ? persisted.isExpanded : true
            });
          }
        } catch (e) {
          console.error('Failed to parse event progress from storage', e);
        }
      }
    },
    saveToStorage() {
      const eventsToSave = state.activeEvents()
        .filter(e => e.progress < 100)
        .map(({ eventId, title, origin, minimized, updatedAt }) => ({
          eventId,
          title,
          origin,
          minimized,
          updatedAt
        }));

      const dataToSave: IPersistedState = {
        events: eventsToSave,
        position: state.widgetPosition(),
        isExpanded: state.isExpanded()
      };
      localStorageFacade.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    },
    updatePosition(x: number, y: number) {
      patchState(state, { widgetPosition: { x, y } });
      this.saveToStorage();
    },
    setExpanded(isExpanded: boolean) {
      patchState(state, { isExpanded });
      this.saveToStorage();
    },
    addEvent(eventId: string, title?: ETypeAutomation | string, origin?: string) {
      const currentEvents = state.activeEvents();
      if (currentEvents.some((e) => e.eventId === eventId)) return;
      
      const newEvents: IEventProgress[] = [
        ...currentEvents,
        { eventId, title: title, origin, progress: 0, minimized: false, updatedAt: DateTime.now().toMillis() },
      ];
      patchState(state, { activeEvents: newEvents });
      this.saveToStorage();
    },
    updateProgress(eventId: string, progress: number, title?: ETypeAutomation | string, origin?: string) {
      const currentEvents = state.activeEvents();
      const existingEvent = currentEvents.find((e) => e.eventId === eventId);

      if (!existingEvent) return;

      const incomingProgress = progress ?? 0;
      const effectiveProgress = Math.max(existingEvent.progress, incomingProgress);

      const newEvents = currentEvents.map((e) =>
        e.eventId === eventId ? { 
          ...e, 
          progress: effectiveProgress, 
          title: title ?? e.title,
          origin: origin || e.origin,
          updatedAt: DateTime.now().toMillis()
        } : e
      );
      
      if (effectiveProgress >= 100) {
        this.removeEvent(eventId);
      } else {
        patchState(state, { activeEvents: newEvents });
        this.saveToStorage();
      }
    },
    removeEvent(eventId: string) {
      const newEvents = state.activeEvents().filter((e) => e.eventId !== eventId);
      patchState(state, { activeEvents: newEvents });
      this.saveToStorage();
    },
    toggleMinimized(eventId: string) {
      const newEvents = state.activeEvents().map((e) =>
        e.eventId === eventId ? { ...e, minimized: !e.minimized } : e
      );
      patchState(state, { activeEvents: newEvents });
      this.saveToStorage();
    },
    clearAll() {
      patchState(state, { activeEvents: [] });
      this.saveToStorage();
    },
    clearStaleEvents() {
      const now = DateTime.now().toMillis();
      const newEvents = state.activeEvents().filter((e) => {
        const updated = e.updatedAt ?? now;
        return (now - updated) < 120_000;
      });
      if (newEvents.length !== state.activeEvents().length) {
        patchState(state, { activeEvents: newEvents });
        this.saveToStorage();
      }
    }
  })),
  withMethods((store, eventProgressService = inject(EventProgressService)) => ({
    listenCrmProgress: rxMethod<void>(
      pipe(
        switchMap(() =>
          keepHubAlive(
            eventProgressService.listenCrmProgress().pipe(
              tap((notification) => {
                const { EventId, Progress, Title } = notification.eventData || {};
                if (EventId) {
                  store.updateProgress(EventId, Progress, Title as ETypeAutomation);
                }
              })
            )
          )
        )
      )
    ),
    listenContractProgress: rxMethod<void>(
      pipe(
        switchMap(() =>
          keepHubAlive(
            eventProgressService.listenContractProgress().pipe(
              tap((notification) => {
                const { EventId, Progress } = notification.eventData || {};
                if (EventId) {
                  store.updateProgress(EventId, Progress);
                }
              })
            )
          )
        )
      )
    ),
  })),
  withHooks((store) => {
    const notificationStore = inject(ClientNotificationStore);

    return {
      onInit() {
        store.loadFromStorage();

        effect(() => {
          const notifications = notificationStore.notificationsMenu().notifications;
          if (!notifications?.length) return;

          const activeEvents = store.activeEvents();
          if (!activeEvents.length) return;

          untracked(() => {
            notifications.forEach((notif) => {
              try {
                const content = typeof notif.Content === 'string' ? JSON.parse(notif.Content) : notif.Content;
                const eventId = content?.EventId;
                const eventName = content?.EventName || notif.Origin;

                const isCompletionEvent =
                  eventName === EClientNotificationsEventTypes.onCrmEventNotification ||
                  eventName === EClientNotificationsEventTypes.onContractEventNotification ||
                  eventName === EClientNotificationsEventTypes.onCentralNotificationContract;

                if (eventId && isCompletionEvent) {
                  const event = activeEvents.find(e => e.eventId === eventId);
                  if (event && event.progress < 100) {
                    store.updateProgress(eventId, 100, event.title);
                  }
                }
              } catch (e) {
              }
            });
          });
        });

        effect(() => {
          const notification = notificationStore.notifications();
          if (!notification) return;

          untracked(() => {
            const { eventName, eventData } = notification;
            const { EventId } = eventData || {};

            if (!EventId) return;

            if (
              eventName === EClientNotificationsEventTypes.onCrmEventNotification ||
              eventName === EClientNotificationsEventTypes.onContractEventNotification ||
              eventName === EClientNotificationsEventTypes.onCentralNotificationContract
            ) {
              store.updateProgress(EventId, 100);
            }
          });
        });

        store.listenCrmProgress();
        store.listenContractProgress();
      },
    };
  })
);
